import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { creatorMissionDropNotifyPath } from "@/routes"

interface NotifyToggleProps {
  /** Server-resolved current preference (default ON). */
  enabled: boolean
  className?: string
}

/**
 * OKS-477 — "Notify me when it drops" pill (Figma `47:258`).
 *
 * A plain Inertia POST (NOT a shadcn Form / react-hook-form / zod — Crew hard
 * rule): toggling fires `router.post` to persist the per-drop preference and
 * shows an OPTIMISTIC local flip + a confirmation toast immediately (UX Spec
 * §4 — "immediate visual feedback"). On a server error the optimistic state
 * rolls back. The toggle gates only the push; the on-screen reveal flip is
 * independent.
 */
export function NotifyToggle({ enabled, className }: NotifyToggleProps) {
  const { t } = useTranslation()
  const [on, setOn] = useState(enabled)
  const [pending, setPending] = useState(false)

  function handleChange(next: boolean) {
    const previous = on
    setOn(next) // optimistic
    setPending(true)

    router.post(
      creatorMissionDropNotifyPath(),
      { notify: next },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          toast.success(
            t(
              next
                ? "mission_drop.notify.toast_on"
                : "mission_drop.notify.toast_off",
            ),
          )
        },
        onError: () => {
          setOn(previous) // rollback
          toast.error(t("mission_drop.notify.toast_error"))
        },
        onFinish: () => setPending(false),
      },
    )
  }

  return (
    <div
      className={cn(
        "mt-4 flex items-center gap-3 rounded-full bg-white/15 px-4 py-2",
        className,
      )}
      data-testid="notify-toggle"
    >
      <Switch
        id="mission-drop-notify"
        checked={on}
        disabled={pending}
        onCheckedChange={handleChange}
        aria-label={t("mission_drop.notify.label")}
        data-testid="notify-toggle-switch"
      />
      <Label
        htmlFor="mission-drop-notify"
        className="cursor-pointer text-sm font-semibold text-white"
      >
        {t("mission_drop.notify.label")} ·{" "}
        {t(on ? "mission_drop.notify.on" : "mission_drop.notify.off")}
      </Label>
    </div>
  )
}
